const API_BASE_URL = process.env.REACT_APP_API_URL || "";

export const contractorContactsService = {
  // Get all contractor contacts with DataTables support
  async getAll(params = {}) {
    try {
      const queryParams = new URLSearchParams();

      // Add DataTables parameters
      if (params.draw) queryParams.append("draw", params.draw);
      if (params.start) queryParams.append("start", params.start);
      if (params.length) queryParams.append("length", params.length);

      // Add search parameter in DataTables format
      if (params.search && params.search.value) {
        queryParams.append("search[value]", params.search.value);
      }

      const response = await fetch(
        `${API_BASE_URL}/api/contractors/contacts?${queryParams}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching contractor contacts:", error);
      throw error;
    }
  },

  // Get contractor contact by ID
  async getById(id) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/contractors/contacts/${id}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching contractor contact:", error);
      throw error;
    }
  },

  // Create new contractor contact
  async create(contactData) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/contractors/contacts`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(contactData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error creating contractor contact:", error);
      throw error;
    }
  },

  // Update contractor contact
  async update(id, contactData) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/contractors/contacts/${id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify(contactData),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error updating contractor contact:", error);
      throw error;
    }
  },

  // Delete contractor contact
  async delete(id) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/contractors/contacts/${id}/destroy`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error deleting contractor contact:", error);
      throw error;
    }
  },

  // Get contractor contact calendar - NEW API matching Laravel Internal API
  async getCalendarData(contactId) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/tickets/contractor-contact/${contactId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching contractor contact calendar:", error);
      throw error;
    }
  },

  // Get contractor contact DataTable (server-side processing)
  async getCalendarDataTable(contactId, tableParams) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/tickets/contractor-contact/${contactId}/datatable`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify(tableParams),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching contractor contact datatable:", error);
      throw error;
    }
  },

  // Update ticket from contractor calendar modal
  async updateTicketFromCalendar(ticketId, updateData) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/tickets/update-from-contractor-calendar/${ticketId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify(updateData),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error updating ticket from calendar:", error);
      throw error;
    }
  },

  // LEGACY: Get contractor contact calendar (old endpoint)
  async getCalendar(id) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/contractors/contacts/${id}/calendar`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching contractor contact calendar:", error);
      throw error;
    }
  },

  // Get mock data for fallback
  getMockData() {
    return [
      {
        id: 1,
        userId: 101,
        name: "Aaron Sandell",
        company: "GGFM",
        email: "Aaron@global-guardians.co.uk",
        phone: "",
        department: "",
        job_title: "Maintenance Operative",
        color: "#18b9db",
        contractor_id: 1,
      },
      {
        id: 2,
        userId: 102,
        name: "Abdul Raheem",
        company: "Global Guardians",
        email: "abdul@global-guardians.co.uk",
        phone: "",
        department: "",
        job_title: "Inspector",
        color: "",
        contractor_id: 2,
      },
      {
        id: 3,
        userId: 103,
        name: "Abishek Balasuriya",
        company: "Global Guardians",
        email: "abishek@global-guardians.co.uk",
        phone: "",
        department: "",
        job_title: "Administrator",
        color: "",
        contractor_id: 2,
      },
      {
        id: 4,
        userId: 104,
        name: "Abraham O'Neill",
        company: "GGFM",
        email: "Abraham.O'Neill@global-guardians.co.uk",
        phone: "",
        department: "",
        job_title: "Maintenance Operative",
        color: "",
        contractor_id: 1,
      },
      {
        id: 5,
        userId: 105,
        name: "Abraham Oneill",
        company: "GGFM",
        email: "abraham@global-guardians.co.uk",
        phone: "",
        department: "",
        job_title: "Maintenance Operative",
        color: "",
        contractor_id: 1,
      },
      {
        id: 6,
        userId: 106,
        name: "Adam Smith",
        company: "Global Guardians",
        email: "adam@global-guardians.co.uk",
        phone: "020 1234 5678",
        department: "Operations",
        job_title: "Manager",
        color: "#ff6b6b",
        contractor_id: 2,
      },
      {
        id: 7,
        userId: 107,
        name: "Alice Johnson",
        company: "GGFM",
        email: "alice@global-guardians.co.uk",
        phone: "020 8765 4321",
        department: "HR",
        job_title: "HR Coordinator",
        color: "#4ecdc4",
        contractor_id: 1,
      },
      {
        id: 8,
        userId: 108,
        name: "Bob Wilson",
        company: "Global Guardians",
        email: "bob@global-guardians.co.uk",
        phone: "",
        department: "",
        job_title: "Security Officer",
        color: "",
        contractor_id: 2,
      },
      {
        id: 9,
        userId: 109,
        name: "Carol Davis",
        company: "GGFM",
        email: "carol@global-guardians.co.uk",
        phone: "020 5555 1234",
        department: "Finance",
        job_title: "Accountant",
        color: "#ffa726",
        contractor_id: 1,
      },
      {
        id: 10,
        userId: 110,
        name: "David Brown",
        company: "Global Guardians",
        email: "david@global-guardians.co.uk",
        phone: "020 4444 5678",
        department: "IT",
        job_title: "System Administrator",
        color: "#9c27b0",
        contractor_id: 2,
      },
      {
        id: 11,
        userId: 111,
        name: "Emma Wilson",
        company: "GGFM",
        email: "emma@global-guardians.co.uk",
        phone: "020 3333 9999",
        department: "Marketing",
        job_title: "Marketing Manager",
        color: "#f44336",
        contractor_id: 1,
      },
      {
        id: 12,
        userId: 112,
        name: "Frank Miller",
        company: "Global Guardians",
        email: "frank@global-guardians.co.uk",
        phone: "020 2222 1111",
        department: "Operations",
        job_title: "Operations Director",
        color: "#2196f3",
        contractor_id: 2,
      },
      {
        id: 13,
        userId: 113,
        name: "Grace Taylor",
        company: "GGFM",
        email: "grace@global-guardians.co.uk",
        phone: "020 1111 2222",
        department: "Customer Service",
        job_title: "Customer Service Manager",
        color: "#4caf50",
        contractor_id: 1,
      },
      {
        id: 14,
        userId: 114,
        name: "Henry Anderson",
        company: "Global Guardians",
        email: "henry@global-guardians.co.uk",
        phone: "020 9999 8888",
        department: "Sales",
        job_title: "Sales Representative",
        color: "#ff9800",
        contractor_id: 2,
      },
      {
        id: 15,
        userId: 115,
        name: "Ivy Chen",
        company: "GGFM",
        email: "ivy@global-guardians.co.uk",
        phone: "020 8888 7777",
        department: "Research",
        job_title: "Research Analyst",
        color: "#795548",
        contractor_id: 1,
      },
    ];
  },

  // Get mock calendar data for fallback
  getMockCalendarData() {
    return {
      success: true,
      data: [
        {
          id: 1,
          status: "completed",
          urgency: "medium",
          property_name: "Property A",
          postcode: "SW1A 1AA",
          submitted: "2025-08-01",
          book_in: "2025-08-02",
          deadline: "2025-08-05",
          completed: "2025-08-03",
          job_type: "General Maintenance",
        },
        {
          id: 2,
          status: "declined",
          urgency: "high",
          property_name: "Property B",
          postcode: "W1A 1AA",
          submitted: "2025-08-02",
          book_in: "2025-08-03",
          deadline: "2025-08-06",
          completed: null,
          job_type: "Electrical Work",
        },
        {
          id: 3,
          status: "completed",
          urgency: "low",
          property_name: "Property C",
          postcode: "E1A 1AA",
          submitted: "2025-08-03",
          book_in: "2025-08-04",
          deadline: "2025-08-07",
          completed: "2025-08-05",
          job_type: "Plumbing",
        },
        {
          id: 4,
          status: "completed",
          urgency: "medium",
          property_name: "Property D",
          postcode: "N1A 1AA",
          submitted: "2025-08-04",
          book_in: "2025-08-05",
          deadline: "2025-08-08",
          completed: "2025-08-06",
          job_type: "Locksmith",
        },
        {
          id: 5,
          status: "completed",
          urgency: "high",
          property_name: "Property E",
          postcode: "SE1A 1AA",
          submitted: "2025-08-05",
          book_in: "2025-08-06",
          deadline: "2025-08-09",
          completed: "2025-08-07",
          job_type: "Fire Risk Installation",
        },
      ],
    };
  },
};

export default contractorContactsService;
